import type { WalletMeta } from "./types";
import {
  getOrCreateEncryptionKey,
  encryptBytes,
  decryptBytes,
  deleteEncryptionKey,
} from "./crypto";

// Use window.localStorage directly -- intentionally bypassing the glideLocalStorage
// helper, which proxies reads/writes to the parent window via postMessage when
// didProvideLocalStorage is true. Private key material must never cross the iframe boundary.

const KEYS = {
  secp256k1: "glide.wallet.enc.secp256k1",
  ed25519: "glide.wallet.enc.ed25519",
  meta: "glide.wallet.meta",
} as const;

export async function saveWallet(
  secp256k1Key: Uint8Array,
  ed25519Key: Uint8Array,
  meta: WalletMeta,
): Promise<void> {
  const encKey = await getOrCreateEncryptionKey();
  const [encSecp, encEd] = await Promise.all([
    encryptBytes(encKey, secp256k1Key),
    encryptBytes(encKey, ed25519Key),
  ]);
  window.localStorage.setItem(KEYS.secp256k1, encSecp);
  window.localStorage.setItem(KEYS.ed25519, encEd);
  window.localStorage.setItem(KEYS.meta, JSON.stringify(meta));
}

export function loadMeta(): WalletMeta | null {
  const raw = window.localStorage.getItem(KEYS.meta);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as WalletMeta;
  } catch {
    return null;
  }
}

export async function loadPrivateKeys(): Promise<{
  secp256k1Key: Uint8Array;
  ed25519Key: Uint8Array;
} | null> {
  const encSecp = window.localStorage.getItem(KEYS.secp256k1);
  const encEd = window.localStorage.getItem(KEYS.ed25519);
  if (!encSecp || !encEd) return null;

  try {
    const encKey = await getOrCreateEncryptionKey();
    const [secp256k1Key, ed25519Key] = await Promise.all([
      decryptBytes(encKey, encSecp),
      decryptBytes(encKey, encEd),
    ]);
    return { secp256k1Key, ed25519Key };
  } catch {
    return null;
  }
}

export function walletExists(): boolean {
  return window.localStorage.getItem(KEYS.meta) !== null;
}

export function markBackedUp(): void {
  const metaRaw = window.localStorage.getItem(KEYS.meta);
  if (!metaRaw) return;
  try {
    const meta = JSON.parse(metaRaw) as WalletMeta;
    window.localStorage.setItem(
      KEYS.meta,
      JSON.stringify({ ...meta, backedUp: true }),
    );
  } catch {
    // ignore
  }
}

export async function deleteWallet(): Promise<void> {
  window.localStorage.removeItem(KEYS.secp256k1);
  window.localStorage.removeItem(KEYS.ed25519);
  window.localStorage.removeItem(KEYS.meta);
  await deleteEncryptionKey();
}
